import {
  isCodeableConcept,
  isCoding,
  isReference,
  isResource,
} from '@medplum/core'
import type {
  CodeableConcept,
  Coding,
  Identifier,
  Reference,
  Resource,
  ResourceType,
} from '@medplum/fhirtypes'
import { addDays } from 'date-fns'
import { compact, isEmpty, isObject, mapValues } from 'lodash-es'
import type { SeenMedplumClient as MedplumClient } from '#src/client.ts'
import { getCodingFromCodeableConcept } from '#src/codeableConcept.ts'
import { isDateString, parseDateString, toDateString } from '#src/dates.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { fhirSearchUrl } from '#src/medplum.ts'
import { getReferenceString } from '#src/references.ts'
import { recordEntries } from '#src/utils.ts'

type QueryTypeValue =
  | string
  | number
  | boolean
  | undefined
  | CodeableConcept
  | Coding
  | Identifier
  | Reference
  | Resource
  | ExtendedResource

export type QueryTypes = Record<string, QueryTypeValue | QueryTypeValue[]>

export function normalizeQuery(query?: QueryTypes): Record<string, string> {
  return mapValues(query, (value, key) => {
    if (Array.isArray(value)) {
      return compact(value.map((v) => mapQueryValueToString(v, key))).join(',')
    }
    return mapQueryValueToString(value, key)
  })
}

function isIdentifier(
  value: QueryTypeValue
): value is { system: string; value: string } {
  return (
    isObject(value) && 'system' in value && typeof value.system === 'string'
  )
}

function mapQueryValueToString(value: QueryTypeValue, key: string): string {
  if (value instanceof ExtendedResource) {
    return value.reference.reference
  } else if (isCodeableConcept(value)) {
    const coding = getCodingFromCodeableConcept(value)
    return systemValueToString({
      system: coding?.system ?? '',
      value: coding?.code,
    })
  } else if (isCoding(value)) {
    return codingToString(value)
  } else if (isIdentifier(value)) {
    return systemValueToString(value)
  } else if (isReference(value) || isResource(value)) {
    return getReferenceString(value)
  }

  if (isObject(value)) {
    if (isEmpty(value)) {
      return ''
    } else {
      console.error(
        `Unsupported object query value: ${JSON.stringify(value)} for key: ${key}`
      )
    }
  }

  // TODO: Confirm with Medplum team the query values they don't support.
  let result = String(value ?? '')
  if (key === 'code:text') {
    result = result.replace(/[:&()|<\s']+/g, '')
  } else if (key === 'name') {
    result = result.replace(/[:&()|<']+/g, '')
  }

  return result
}

export function codingToString({ system, code }: Coding): string {
  return systemValueToString({ system: system ?? '', value: code ?? '' })
}

function systemValueToString({
  system,
  value,
}: {
  system: string
  value: string | undefined
}): string {
  if (system && value) {
    return `${system}|${value}`
  }
  if (system) {
    return `${system}|`
  }
  if (value) {
    return value
  }
  return ''
}

// Medplum reads one prefix per date value and then validates the remainder, so
// `ge<from>,le<to>` is rejected; `_filter` is the only shape that ANDs two
// bounds on a single search parameter. Medplum also compares a bare date as
// that day's midnight, so `le <day>` would drop everything later on that day;
// a date-only `to` is sent as `lt <next day>` to keep the whole day.
export function dateRangeFilter(
  searchParam: string,
  { from, to }: { from?: string; to?: string }
): QueryTypes {
  const clauses = compact([
    from ? `${searchParam} ge ${from}` : undefined,
    to ? upperBoundClause(searchParam, to) : undefined,
  ])

  return clauses.length > 0 ? { _filter: clauses.join(' and ') } : {}
}

function upperBoundClause(searchParam: string, to: string): string {
  return isDateString(to)
    ? `${searchParam} lt ${toDateString(addDays(parseDateString(to), 1))}`
    : `${searchParam} le ${to}`
}

export function queryTypesToString(query: QueryTypes): string {
  const searchParams = new URLSearchParams()
  recordEntries(normalizeQuery(query)).forEach(([key, value]) => {
    searchParams.set(key, value)
  })

  return searchParams.toString()
}

export function getSearchKey(
  medplum: MedplumClient,
  type: ResourceType,
  query: Record<string, string> | undefined
): string {
  return fhirSearchUrl(type, query, medplum).toString()
}
